import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { HOST_API } from 'config';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { closeLoading, showLoading } from 'reducer/common/loadingCount';
import { EnumAppRouter } from 'router/enum';
import { isArray, isBlob, isObject } from 'tradevan-ui/consts';
import { Iobject } from 'tradevan-ui/type';
import {
  IaxiosParam,
  IblobAxios,
  IdoAxios,
  IdoAxiosReturn,
  Iresponse,
  MethodEnum,
} from './type';

const useAxios = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  let loadingFlag = true;
  // #region axios setting
  const instance = axios.create({
    withCredentials: true,
    baseURL: HOST_API,
    headers: { 'Content-Type': 'application/json' },
    timeout: 60000,
  });

  // #region axios setting
  // #region request 攔截器
  instance.interceptors.request.use(
    (config) => {
      if (loadingFlag) {
        dispatch(showLoading());
      }
      const tokenConfig = {
        ...config,
        headers: { ...config.headers },
      };
      return tokenConfig;
    },
    (error) => {
      dispatch(closeLoading());
      return Promise.reject(error);
    },
  );
  // #endregion

  // #region response 攔截器

  instance.interceptors.response.use(
    (response) => {
      dispatch(closeLoading());
      return response;
    },
    async (error) => {
      dispatch(closeLoading());
      // #region 如果 refreshToken也回傳401，導到錯誤頁
      const targetUrl = error.request.responseURL;
      if (targetUrl === `${HOST_API}token/refreshToken`) {
        navigate(EnumAppRouter.錯誤頁);
        return Promise.reject(error);
      }
      // #endregion

      // 呼叫API RefreshToken
      switch (error.response?.status) {
        case 401:
          try {
            // 取得新token
            await instance.post<Iresponse>('/token/refreshToken');
          } catch (_error) {
            return Promise.reject(_error);
          }
          break;
        default:
          break;
      }
      return Promise.reject(error);
    },
  );

  // #endregion

  const doAxios: IdoAxios = (
    method,
    url,
    data,
    doLoading = true,
    config = {},
  ) => {
    loadingFlag = doLoading;
    switch (method) {
      case MethodEnum.post:
        return instance.post(url, data, config);
      case MethodEnum.get:
        return instance.get(url, {
          params: data,
          ...config,
          paramsSerializer: (params) => {
            let result = '';
            Object.keys(params).forEach((key) => {
              result += `${key}=${encodeURIComponent(params[key])}&`;
            });
            return result.substring(0, result.length - 1);
          },
        });
      case 'delete':
        return instance.delete(url, { params: data });
      case 'put':
        return instance.put(url, data);
      case 'patch':
        return instance.patch(url, data);
      default:
        return instance.get(url, { params: data });
    }
  };

  /** 物件轉成 formData */
  const objToFormData = (data: Iobject) => {
    const formData = new FormData();
    Object.keys(data).forEach((obj) => {
      const temp = data[obj];
      formData.append(
        obj,
        isArray(temp) || (isObject(temp) && !isBlob(temp))
          ? JSON.stringify(temp)
          : (temp as string | Blob),
      );
    });
    return formData;
  };

  const formBlobAxios = <T = Blob>(
    url: IaxiosParam['url'],
    data: Record<string, unknown>,
    config: IaxiosParam['config'] = { responseType: 'blob' },
  ): Promise<AxiosResponse<T> | undefined> => {
    const { headers = {} } = config || {};
    return instance.post(url, data, {
      ...config,
      headers: { ...headers, 'Content-Type': 'multipart/form-data' },
    });
  };

  const formAxios = <T>(
    method: IaxiosParam['method'],
    url: IaxiosParam['url'],
    data: Record<string, unknown>,
    config: IaxiosParam['config'] = {},
  ): IdoAxiosReturn<T> => {
    const { headers = {} } = config || {};
    return doAxios<unknown, T>(method, url, objToFormData(data), loadingFlag, {
      ...config,
      headers: { ...headers, 'Content-Type': 'multipart/form-data' },
    });
  };

  const blobAxios: IblobAxios = (method, url, data, config = {}) => {
    const currentConfig: AxiosRequestConfig = {
      ...config,
      responseType: 'blob',
    };
    switch (method.toLowerCase()) {
      case MethodEnum.post:
        return instance.post(url, data, currentConfig);
      case MethodEnum.get:
        return instance.get(url, {
          params: data,
          ...currentConfig,
          paramsSerializer: (params) => {
            let result = '';
            Object.keys(params).forEach((key) => {
              result += `${key}=${encodeURIComponent(params[key])}&`;
            });
            return result.substr(0, result.length - 1);
          },
        });
      default:
        return instance.get(url, { params: data });
    }
  };
  /** 下載 api blob  */
  const blobDownload = (
    response: AxiosResponse<Blob>,
    // enqueueSnackbar: ProviderContext['enqueueSnackbar'],
    _fileName?: string,
  ) => {
    // { responseType: "blob" }
    // Access-Control-Expose-Headers : Content-Disposition, msg
    let fileName = _fileName || '';
    if (!fileName) {
      if (!response.headers['content-disposition']) {
        const msgObj = response.headers.msg;
        if (msgObj) {
          try {
            // const res = JSON.parse(decodeURIComponent(msgObj));
            // enqueueSnackbar(res.msg);
          } catch (e) {
            //
            // enqueueSnackbar('系統忙碌中，請稍後再試...');
          }
        }
        return;
      }
      fileName = response.headers['content-disposition']
        .split('=')[1]
        .split(';')[0]
        .replace(/"/g, '');
    }

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(new Blob([response.data]));
    link.setAttribute('download', decodeURIComponent(fileName)); // or any other extension
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(link.href);
  };

  return {
    formBlobAxios,
    doAxios,
    formAxios,
    blobAxios,
    blobDownload,
    objToFormData,
  };
};

export default useAxios;
